import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react';
import { PasteUploadPreview, usePasteUploadPreview } from './PasteUploadPreview';

/**
 * Rich text, the small way: bodies are stored as a sanitized HTML subset
 * (bold, italic, underline, lists, links, paragraphs). Everything else —
 * scripts, styles, fonts, colors, event handlers, unknown tags — is stripped
 * on every write AND every render, so the database never holds anything the
 * interface would not willingly draw.
 *
 * Pasted content is normalized through the same door: Google Docs, Slack,
 * and AI chat exports arrive as a soup of styled spans; what lands in the
 * editor is the app's own clean formatting with the meaning kept (bold stays
 * bold, bullets stay bullets) and the noise dropped (fonts, sizes, colors).
 */

const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'UL',
  'OL',
  'LI',
  'A',
  'P',
  'BR',
  'DIV',
  'IMG',
]);

/**
 * Where a pasted screenshot is allowed to load from. Comments are user-written
 * HTML, so <img> is the one element that can make a reader's browser fetch
 * from somewhere else — a tracking pixel, or a URL that leaks who opened a
 * supplier thread. Only our own Drive-backed hosts are permitted; every other
 * src drops the image entirely rather than rendering it.
 */
const ALLOWED_IMAGE_HOSTS = new Set([
  'drive.google.com',
  'lh3.googleusercontent.com',
  'drive.usercontent.google.com',
]);

export function isAllowedImageSrc(src: string): boolean {
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && ALLOWED_IMAGE_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

/**
 * GIPHY's media hosts, for GIFs picked with the comment box's GIF button
 * (Messaging Drop 3b). Exactly media.giphy.com, media0.giphy.com to
 * media9.giphy.com and i.giphy.com. Not giphy.com itself, not api.giphy.com,
 * and nothing that merely ends in giphy.com.
 */
const GIPHY_MEDIA_HOST = /^(?:media[0-9]?|i)\.giphy\.com$/;

/** True for an https GIPHY media URL on the default port. */
export function isGiphyMediaSrc(src: string): boolean {
  try {
    const url = new URL(src);
    return (
      url.protocol === 'https:' &&
      url.port === '' &&
      url.username === '' &&
      url.password === '' &&
      GIPHY_MEDIA_HOST.test(url.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Where an image in a posted COMMENT may load from: our Drive hosts, plus
 * GIPHY's media hosts for GIFs. Used only by the comment renderer
 * (features/comments/commentHtml.ts), which is what Messages, task and
 * project comments and the Alex Huddle page draw with.
 *
 * isAllowedImageSrc above stays Drive only on purpose. It guards
 * sanitizeRichText, which is what supplier threads, descriptions and the
 * huddle link's own write path use, and none of those have a GIF button. A
 * GIPHY image pasted into a supplier thread is still dropped, so a supplier's
 * browser never fetches from GIPHY.
 */
export function isAllowedCommentImageSrc(src: string): boolean {
  return isAllowedImageSrc(src) || isGiphyMediaSrc(src);
}

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

/** Tags whose CONTENT is also noise — dropping the tag must drop the text. */
const DROPPED_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'META', 'TITLE']);

/** What an element's inline styles MEAN, so the meaning survives the strip. */
function styleIntent(element: HTMLElement): {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  notBold: boolean;
} {
  const style = element.getAttribute('style') ?? '';
  return {
    bold: /font-weight\s*:\s*(bold|bolder|[6-9]00)/i.test(style),
    notBold: /font-weight\s*:\s*(normal|lighter|[1-4]00)/i.test(style),
    italic: /font-style\s*:\s*italic/i.test(style),
    underline: /text-decoration(?:-line)?\s*:[^;]*underline/i.test(style),
  };
}

function sanitizeNode(node: Node, doc: Document): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    // contentEditable pads trailing spaces and mention chips with no-break
    // spaces; serialized they become "&nbsp;", which the legacy plain-text
    // render path then shows literally. Plain spaces read the same and
    // survive every path.
    return doc.createTextNode((node.textContent ?? '').replace(/\u00a0/g, ' '));
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const element = node as HTMLElement;
  const tag = element.tagName;
  if (DROPPED_WITH_CONTENT.has(tag)) {
    return null;
  }
  const intent = styleIntent(element);

  // Google Docs wraps entire documents in <b style="font-weight:normal">;
  // honoring the style, not the tag, is what keeps pastes from turning bold.
  const isRealBold = (tag === 'B' || tag === 'STRONG') && !intent.notBold;

  let target: Node;
  if (HEADING_TAGS.has(tag)) {
    // Headings become bold paragraphs: the emphasis survives, the font noise
    // does not.
    const p = doc.createElement('p');
    const strong = doc.createElement('strong');
    p.appendChild(strong);
    target = p;
    for (const child of Array.from(element.childNodes)) {
      const clean = sanitizeNode(child, doc);
      if (clean !== null) {
        strong.appendChild(clean);
      }
    }
    return target;
  }

  const keep =
    ALLOWED_TAGS.has(tag) && (tag !== 'B' || isRealBold) && (tag !== 'STRONG' || isRealBold);
  if (keep) {
    target = doc.createElement(tag === 'DIV' ? 'p' : tag.toLowerCase());
    if (tag === 'A') {
      const href = element.getAttribute('href') ?? '';
      if (/^https?:\/\//i.test(href)) {
        (target as HTMLElement).setAttribute('href', href);
        (target as HTMLElement).setAttribute('rel', 'noreferrer noopener');
        (target as HTMLElement).setAttribute('target', '_blank');
      }
    }
    if (tag === 'IMG') {
      const src = element.getAttribute('src') ?? '';
      if (!isAllowedImageSrc(src)) {
        // Not ours: drop the image rather than let it fetch. Nothing is
        // carried over, so no handler or foreign URL survives the strip.
        return null;
      }
      (target as HTMLElement).setAttribute('src', src);
      (target as HTMLElement).setAttribute('alt', element.getAttribute('alt') ?? 'Pasted image');
      (target as HTMLElement).setAttribute('loading', 'lazy');
      // Attributes are set explicitly and never copied, so onerror and friends
      // cannot ride along on a pasted tag.
      return target;
    }
  } else {
    // A dropped tag can still carry meaning in its styles (Google Docs says
    // bold with <span style="font-weight:700">). Keep the meaning as
    // semantic wrappers around the children.
    target = doc.createDocumentFragment();
  }

  // Where the children land: inside any style-derived semantic wrappers.
  let mount: Node = target;
  const wraps: string[] = [];
  if (intent.bold && !keep) wraps.push('strong');
  if (intent.italic && tag !== 'I' && tag !== 'EM') wraps.push('em');
  if (intent.underline && tag !== 'U' && tag !== 'A') wraps.push('u');
  for (const wrap of wraps) {
    const wrapper = doc.createElement(wrap);
    mount.appendChild(wrapper);
    mount = wrapper;
  }

  for (const child of Array.from(element.childNodes)) {
    const clean = sanitizeNode(child, doc);
    if (clean !== null) {
      mount.appendChild(clean);
    }
  }
  return target;
}

/** Sanitizes an HTML string down to the allowed subset. */
export function sanitizeRichText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out = doc.implementation.createHTMLDocument('');
  const root = out.createElement('div');
  for (const child of Array.from(doc.body.childNodes)) {
    const clean = sanitizeNode(child, out);
    if (clean !== null) {
      root.appendChild(clean);
    }
  }
  // Runs of empty paragraphs (a Google Docs paste signature) collapse to
  // one blank line; trailing empties are editing artifacts, not content.
  let previousEmpty = false;
  for (const child of Array.from(root.children)) {
    const empty = child.tagName === 'P' && (child.textContent ?? '').trim() === '';
    if (empty && previousEmpty) {
      child.remove();
    } else {
      previousEmpty = empty;
    }
  }
  while (
    root.lastElementChild !== null &&
    root.lastElementChild.tagName === 'P' &&
    (root.lastElementChild.textContent ?? '').trim() === ''
  ) {
    root.lastElementChild.remove();
  }
  return root.innerHTML;
}

function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Plain text to clean HTML. Bulleted and numbered lines (the shape of an AI
 * answer or a Slack message copied as text) become real lists.
 */
export function plainTextToHtml(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const parts: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  function flushList() {
    if (list !== null) {
      const items = list.items.map((item) => `<li>${item}</li>`).join('');
      parts.push(`<${list.kind}>${items}</${list.kind}>`);
      list = null;
    }
  }

  for (const line of lines) {
    const bullet = /^\s*[-*•·]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet) {
      if (list === null || list.kind !== 'ul') {
        flushList();
        list = { kind: 'ul', items: [] };
      }
      list.items.push(escapeText(bullet[1] ?? ''));
    } else if (numbered) {
      if (list === null || list.kind !== 'ol') {
        flushList();
        list = { kind: 'ol', items: [] };
      }
      list.items.push(escapeText(numbered[1] ?? ''));
    } else {
      flushList();
      parts.push(`<p>${line.trim() === '' ? '<br>' : escapeText(line)}</p>`);
    }
  }
  flushList();
  return sanitizeRichText(parts.join(''));
}

/** Legacy plain-text bodies become paragraphs; HTML passes through. */
export function toRichHtml(value: string): string {
  if (value.trim() === '') {
    return '';
  }
  // A body is HTML when it carries tags OR character entities. Tagless
  // bodies saved by the editor can still hold "&nbsp;" (a one-line comment
  // serializes with no wrapper); routing those through the plain-text branch
  // double-escapes the ampersand and prints the entity literally.
  if (value.includes('<') || /&(?:[a-z][a-z0-9]*|#\d+|#x[0-9a-f]+);/i.test(value)) {
    return sanitizeRichText(value);
  }
  return value
    .split('\n')
    .map((line) => `<p>${line === '' ? '<br>' : escapeText(line)}</p>`)
    .join('');
}

/** True when the rich value holds no visible text. */
export function isRichTextEmpty(value: string): boolean {
  const doc = new DOMParser().parseFromString(value, 'text/html');
  return (doc.body.textContent ?? '').trim() === '';
}

/** The visible text of a rich value, for mention matching and previews. */
export function richTextToPlain(value: string): string {
  const doc = new DOMParser().parseFromString(toRichHtml(value), 'text/html');
  return doc.body.textContent ?? '';
}

/** Read-only renderer for stored bodies. */
export function RichTextView({ value }: { value: string }) {
  if (value.trim() === '') {
    return <p className="task-panel-text">—</p>;
  }
  return (
    <div
      className="rich-text-view"
      // Sanitized on every render; see sanitizeRichText.
      dangerouslySetInnerHTML={{ __html: toRichHtml(value) }}
    />
  );
}

const TOOLBAR: { label: string; title: string; command: string; arg?: boolean }[] = [
  { label: 'B', title: 'Bold', command: 'bold' },
  { label: 'I', title: 'Italic', command: 'italic' },
  { label: 'U', title: 'Underline', command: 'underline' },
  { label: '•', title: 'Bulleted list', command: 'insertUnorderedList' },
  { label: '1.', title: 'Numbered list', command: 'insertOrderedList' },
  { label: '🔗', title: 'Link', command: 'createLink', arg: true },
];

export interface MentionCandidate {
  id: string;
  label: string;
}

/** The @token the caret is currently inside the editable area, if any. */
interface ActiveToken {
  node: Text;
  start: number;
  end: number;
  query: string;
}

function findActiveToken(root: HTMLElement): ActiveToken | null {
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0 || !selection.isCollapsed) {
    return null;
  }
  const node = selection.anchorNode;
  if (node === null || node.nodeType !== Node.TEXT_NODE || !root.contains(node)) {
    return null;
  }
  const upToCaret = (node.textContent ?? '').slice(0, selection.anchorOffset);
  const match = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(upToCaret);
  if (!match) {
    return null;
  }
  const query = match[2] ?? '';
  return {
    node: node as Text,
    start: selection.anchorOffset - query.length - 1,
    end: selection.anchorOffset,
    query: query.toLowerCase(),
  };
}

/**
 * The shared rich text editor (descriptions, comments, shared messages).
 * Emits sanitized HTML through onChange; the caller saves it like any other
 * text field. Paste is normalized through the sanitizer, so outside
 * formatting arrives as the app's own.
 *
 * With mentionCandidates set, typing @ offers people; a pick inserts the
 * @name and reports through onMentionPick — who is actually told is decided
 * elsewhere (the caller's mention rows and the database triggers).
 */
export function RichTextEditor({
  label,
  value,
  onChange,
  placeholder,
  mentionCandidates,
  onMentionPick,
  onPasteImage,
  onPasteNeedsReconnect,
  onUploadingChange,
  extraTools,
}: {
  label: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  mentionCandidates?: MentionCandidate[];
  onMentionPick?: (person: MentionCandidate) => void;
  /**
   * Uploads a pasted screenshot and resolves to the URL it now lives at.
   * Editors without this prop keep the old behaviour: images are dropped.
   */
  onPasteImage?: (file: File) => Promise<string>;
  /** Called with true when the paste failed only because Drive needs reconnecting. */
  onPasteNeedsReconnect?: (needed: boolean) => void;
  /** True while a pasted screenshot uploads, so the caller can hold Send and Save. */
  onUploadingChange?: (uploading: boolean) => void;
  /**
   * More toolbar buttons from the caller (the emoji button on supplier review
   * messages). They get insertText, which puts plain text at the caret. Passed
   * in rather than imported so this shared component stays free of features.
   */
  extraTools?: (insertText: (text: string) => void) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastEmitted = useRef<string | null>(null);
  const lastCaret = useRef<Range | null>(null);
  const [token, setToken] = useState<ActiveToken | null>(null);
  const upload = usePasteUploadPreview(onUploadingChange);
  const [pasteError, setPasteError] = useState('');

  // Seed the editable area only when the value changes from outside.
  useEffect(() => {
    if (ref.current !== null && value !== lastEmitted.current) {
      ref.current.innerHTML = toRichHtml(value);
      lastEmitted.current = value;
    }
  }, [value]);

  function emit() {
    if (ref.current === null) {
      return;
    }
    const clean = sanitizeRichText(ref.current.innerHTML);
    lastEmitted.current = clean;
    onChange(clean);
  }

  function syncToken() {
    if (mentionCandidates === undefined || ref.current === null) {
      return;
    }
    setToken(findActiveToken(ref.current));
  }

  const suggestions =
    token === null || mentionCandidates === undefined
      ? []
      : mentionCandidates
          .filter((person) => person.label.toLowerCase().includes(token.query))
          .slice(0, 6);

  function pick(person: MentionCandidate) {
    if (token === null || ref.current === null) {
      return;
    }
    const range = document.createRange();
    range.setStart(token.node, token.start);
    range.setEnd(token.node, token.end);
    range.deleteContents();
    const inserted = document.createTextNode(`@${person.label} `);
    range.insertNode(inserted);
    const selection = window.getSelection();
    if (selection !== null) {
      const caret = document.createRange();
      caret.setStart(inserted, inserted.length);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
    }
    setToken(null);
    onMentionPick?.(person);
    emit();
    ref.current.focus();
  }

  /**
   * Plain text at the caret, or at the end when the caret is not in the
   * editor. A text node, never markup, the same way pick() inserts a mention.
   */
  function insertText(text: string) {
    const root = ref.current;
    if (root === null) {
      return;
    }
    const selection = window.getSelection();
    let range: Range;
    if (
      selection !== null &&
      selection.rangeCount > 0 &&
      root.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      range = selection.getRangeAt(0);
      range.deleteContents();
    } else if (
      lastCaret.current !== null &&
      root.contains(lastCaret.current.commonAncestorContainer)
    ) {
      // The picker took focus; go back to where the caret was at blur.
      range = lastCaret.current;
      range.deleteContents();
    } else {
      // End of the last paragraph, so the text joins the last line.
      const last = root.lastElementChild;
      range = document.createRange();
      range.selectNodeContents(last !== null && last.tagName === 'P' ? last : root);
      range.collapse(false);
    }
    const inserted = document.createTextNode(text);
    range.insertNode(inserted);
    // Focus first: focusing can move the caret, so place it afterwards.
    root.focus();
    if (selection !== null) {
      const caret = document.createRange();
      caret.setStart(inserted, inserted.length);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
    }
    emit();
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (token !== null && suggestions.length > 0 && event.key === 'Enter') {
      event.preventDefault();
      const first = suggestions[0];
      if (first) {
        pick(first);
      }
      return;
    }
    if (token !== null && event.key === 'Escape') {
      event.stopPropagation();
      setToken(null);
    }
  }

  /**
   * A screenshot on the clipboard arrives as a file, not as markup. It is
   * uploaded first and only then inserted, because the editor's value is
   * sanitized HTML: a blob: or data: src would be stripped on the way out and
   * the image would vanish the moment the comment was saved. The picture
   * shows at once as a preview BESIDE the editable area (never inside it),
   * which replaced the old "Uploading image..." line (Option A, Kyu 4 Oct).
   */
  async function pasteImage(file: File) {
    if (onPasteImage === undefined) {
      return;
    }
    setPasteError('');
    onPasteNeedsReconnect?.(false);
    try {
      const src = await upload.track(file, () => onPasteImage(file));
      if (!isAllowedImageSrc(src)) {
        setPasteError('That image could not be attached.');
        return;
      }
      const img = document.createElement('img');
      img.setAttribute('src', src);
      img.setAttribute('alt', 'Pasted image');
      document.execCommand('insertHTML', false, img.outerHTML);
      emit();
    } catch (cause) {
      // The uploader knows why it failed; passing its message through is the
      // difference between "sign in again" and a dead-end "try again".
      const message =
        cause instanceof Error && cause.message !== ''
          ? cause.message
          : 'That image could not be uploaded. Try again.';
      setPasteError(message);
      // Advice is not a fix. When the only remedy is reconnecting Drive, the
      // editor says so upward so the surface can render the button that does
      // it, rather than leaving a dead-end sentence in a text field.
      onPasteNeedsReconnect?.(/needs reconnecting/i.test(message));
    }
  }

  function onPaste(event: ClipboardEvent<HTMLDivElement>) {
    const imageFile = Array.from(event.clipboardData.files).find((file) =>
      file.type.startsWith('image/'),
    );
    if (imageFile !== undefined && onPasteImage !== undefined) {
      event.preventDefault();
      void pasteImage(imageFile);
      return;
    }
    event.preventDefault();
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    const clean = html.trim() !== '' ? sanitizeRichText(html) : plainTextToHtml(text);
    if (clean === '') {
      return;
    }
    document.execCommand('insertHTML', false, clean);
    emit();
  }

  function run(command: string, needsArg: boolean | undefined) {
    if (needsArg) {
      const url = window.prompt('Link address (https://…)');
      if (url === null || url.trim() === '') {
        return;
      }
      document.execCommand(command, false, url.trim());
    } else {
      document.execCommand(command);
    }
    emit();
  }

  return (
    <div className="rich-text-editor">
      <div
        ref={ref}
        className="rich-text-area"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        {...(placeholder !== undefined ? { 'data-placeholder': placeholder } : {})}
        onInput={() => {
          emit();
          syncToken();
        }}
        onBlur={() => {
          // Where the caret was, for a toolbar pick after focus moved away.
          const selection = window.getSelection();
          lastCaret.current =
            selection !== null && selection.rangeCount > 0
              ? selection.getRangeAt(0).cloneRange()
              : null;
          emit();
        }}
        onKeyUp={syncToken}
        onClick={syncToken}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      <PasteUploadPreview src={upload.preview} />
      {pasteError !== '' && <p className="rich-text-note rich-text-note--error">{pasteError}</p>}

      {suggestions.length > 0 ? (
        <ul className="mention-list" role="listbox" aria-label="People to mention">
          {suggestions.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="mention-option"
                // Mouse down, not click: click fires after the editor blur
                // has already closed the list.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(person);
                }}
              >
                {person.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        {TOOLBAR.map((tool) => (
          <button
            key={tool.command}
            type="button"
            className="rich-text-tool"
            title={tool.title}
            aria-label={tool.title}
            // Keep focus (and the selection) inside the editable area.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => run(tool.command, tool.arg)}
          >
            {tool.label}
          </button>
        ))}
        {extraTools?.(insertText)}
      </div>
    </div>
  );
}
